import React, { useState, useEffect } from 'react';
import { Plus, ArrowUp, ArrowDown, Trash2, Save, Edit, MapPin, Route as RouteIcon, Clock, Navigation, ArrowRightLeft, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl, API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL + '/api';

interface TrainLine {
  id: number;
  name: string;
  color: string;
  company_name: string;
  region_name?: string;
  region_country?: string;
}

interface Region {
  id: number;
  name: string;
  country: string;
}

interface Company {
  id: number;
  name: string;
  code: string;
  region_name?: string;
}

interface Station {
  id: number;
  name: string;
  code?: string;
  line_id: number;
}

interface RouteSegment {
  id: number;
  from_station_id: number;
  to_station_id: number;
  from_station_name?: string;
  to_station_name?: string;
  segment_order: number;
  distance_km: string;
  duration_minutes: number;
  transport_type: string;
  status: string;
}

interface TrainRoute {
  id: number;
  line_id: number;
  name: string;
  description: string;
  total_distance_km?: string;
  total_duration_minutes?: number;
  line_name: string;
  line_color: string;
  route_segments: RouteSegment[];
}

interface IntersectionPoint {
  id: number;
  name: string;
  description: string;
  lat?: number;
  long?: number;
  station_codes?: string[];
  accessibility_features?: any;
  status: string;
  created_at: string;
  updated_at: string;
}

interface IntersectionSegment {
  id: number;
  intersection_point_id: number;
  from_station_id: number;
  to_station_id: number;
  from_line_id: number;
  to_line_id: number;
  distance_km: string;
  duration_minutes: number;
  transport_type: string;
  transfer_cost?: string;
  direction_instructions?: string;
  accessibility_notes?: string;
  status: string;
  created_at: string;
  updated_at: string;
  from_station_name?: string;
  to_station_name?: string;
  from_line_name?: string;
  to_line_name?: string;
  from_line_color?: string;
  to_line_color?: string;
  intersection_point_name?: string;
}

const RouteManagementManager: React.FC = () => {
  const [lines, setLines] = useState<TrainLine[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [selectedLine, setSelectedLine] = useState<number | null>(null);
  const [currentRoute, setCurrentRoute] = useState<TrainRoute | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateRoute, setShowCreateRoute] = useState(false);
  const [showAddSegment, setShowAddSegment] = useState(false);
  const [editingSegment, setEditingSegment] = useState<RouteSegment | null>(null);
  const [showAddIntersection, setShowAddIntersection] = useState(false);


  // Intersection states
  const [intersectionPoints, setIntersectionPoints] = useState<IntersectionPoint[]>([]);
  const [intersectionSegments, setIntersectionSegments] = useState<IntersectionSegment[]>([]);
  const [editingIntersectionPoint, setEditingIntersectionPoint] = useState<IntersectionPoint | null>(null);

  // Filter states
  const [regions, setRegions] = useState<Region[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  // const [showCreatePoint, setShowCreatePoint] = useState(false);
  // const [showCreateIntersectionSegment, setShowCreateIntersectionSegment] = useState(false);
  // const [editingIntersectionSegment, setEditingIntersectionSegment] = useState<IntersectionSegment | null>(null);

  // Form states
  const [routeForm, setRouteForm] = useState({
    name: '',
    description: ''
  });
  const [segmentForm, setSegmentForm] = useState({
    from_station_id: '',
    to_station_id: '',
    distance_km: '',
    duration_minutes: '',
    transport_type: 'train'
  });

  // Intersection form states
  const [pointForm, setPointForm] = useState({
    name: '',
    description: '',
    lat: '',
    long: '',
    station_codes: '',
    accessibility_features: ''
  });

  const [intersectionSegmentForm, setIntersectionSegmentForm] = useState({
    intersection_point_id: '',
    from_station_id: '',
    to_station_id: '',
    from_line_id: '',
    to_line_id: '',
    distance_km: '',
    duration_minutes: '',
    transport_type: 'walk',
    transfer_cost: '0.00',
    direction_instructions: '',
    accessibility_notes: ''
  });

  const [selectedOtherLine, setSelectedOtherLine] = useState<number | null>(null);
  const [otherLineStations, setOtherLineStations] = useState<Station[]>([]);

  const { token } = useAuth();

  // Fetch data functions
  const fetchLines = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRegion) params.append('region_id', selectedRegion.toString());
      if (selectedCompany) params.append('company_id', selectedCompany.toString());

      const url = `${API_BASE_URL}/lines/${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setLines(data.lines || []);
    } catch (err) {
      setError('Failed to fetch lines');
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/regions/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRegions(data.regions || []);
    } catch (err) {
      setError('Failed to fetch regions');
    }
  };

  const fetchCompanies = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRegion) params.append('region_id', selectedRegion.toString());

      const url = `${API_BASE_URL}/companies/${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCompanies(data.companies || []);
    } catch (err) {
      setError('Failed to fetch companies');
    }
  };

  const fetchStations = async (lineId?: number) => {
    try {
      const url = lineId
        ? `${API_BASE_URL}/stations/?line_id=${lineId}`
        : `${API_BASE_URL}/stations/`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStations(data.stations || []);
    } catch (err) {
      setError('Failed to fetch stations');
    }
  };

  const fetchOtherLineStations = async (lineId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/stations/?line_id=${lineId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setOtherLineStations(data.stations || []);
    } catch (err) {
      setError('Failed to fetch other line stations');
    }
  };

  // Removed unused fetchRoutes function

  const fetchRouteForLine = async (lineId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/route-management/routes/line/${lineId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentRoute(data);
      } else {
        setCurrentRoute(null);
      }
    } catch (err) {
      setCurrentRoute(null);
    }
  };

  // CRUD operations
  const createRoute = async () => {
    if (!selectedLine) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/route-management/routes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          line_id: selectedLine,
          name: routeForm.name,
          description: routeForm.description
        })
      });

      if (response.ok) {
        await fetchRouteForLine(selectedLine);
        setShowCreateRoute(false);
        setRouteForm({ name: '', description: '' });
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create route');
      }
    } catch (err) {
      setError('Failed to create route');
    } finally {
      setLoading(false);
    }
  };

  const addSegment = async () => {
    if (!currentRoute) return;

    // Validate form data
    const fromStationId = parseInt(segmentForm.from_station_id);
    const toStationId = parseInt(segmentForm.to_station_id);
    const distanceKm = parseFloat(segmentForm.distance_km);
    const durationMinutes = parseInt(segmentForm.duration_minutes);

    if (!fromStationId || fromStationId <= 0) {
      setError('Please select a valid from station');
      return;
    }
    if (!toStationId || toStationId <= 0) {
      setError('Please select a valid to station');
      return;
    }
    if (fromStationId === toStationId) {
      setError('From and to stations must be different');
      return;
    }
    if (!distanceKm || distanceKm <= 0) {
      setError('Please enter a valid distance (greater than 0)');
      return;
    }
    if (!durationMinutes || durationMinutes <= 0) {
      setError('Please enter a valid duration (greater than 0)');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/route-management/routes/${currentRoute.id}/segments/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          from_station_id: fromStationId,
          to_station_id: toStationId,
          distance_km: distanceKm,
          duration_minutes: durationMinutes,
          transport_type: segmentForm.transport_type || 'train'
        })
      });

      if (response.ok) {
        // Refresh route data to show new segment
        await fetchRouteForLine(currentRoute.line_id);
        // Reset form and hide add segment form
        setShowAddSegment(false);
        setSegmentForm({
          from_station_id: '',
          to_station_id: '',
          distance_km: '',
          duration_minutes: '',
          transport_type: 'train'
        });
        // Show success message briefly
        alert('Segment saved successfully!');
      } else {
        try {
          const errorData = await response.json();
          setError(typeof errorData === 'string' ? errorData : (errorData?.detail || `Failed to add segment (${response.status})`));
        } catch {
          setError(`Failed to add segment (${response.status})`);
        }
      }
    } catch (err) {
      setError('Failed to add segment');
    } finally {
      setLoading(false);
    }
  };

  const moveSegment = async (segmentId: number, direction: 'up' | 'down') => {
    if (!currentRoute) return;

    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/route-management/segments/${segmentId}/move`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ direction })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Successfully moved, refresh the route data
          await fetchRouteForLine(currentRoute.line_id);
        } else {
          setError(result.message || `Cannot move segment ${direction}`);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to move segment');
      }
    } catch (err) {
      setError('Failed to move segment');
    } finally {
      setLoading(false);
    }
  };

  const updateSegment = async (segment: RouteSegment) => {
    if (!currentRoute) return;

    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/route-management/segments/${segment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          from_station_id: segment.from_station_id,
          to_station_id: segment.to_station_id,
          distance_km: parseFloat(segment.distance_km),
          duration_minutes: segment.duration_minutes,
          transport_type: segment.transport_type
        })
      });

      if (response.ok) {
        // Successfully updated, refresh the route data
        await fetchRouteForLine(currentRoute.line_id);
        setEditingSegment(null);
        alert('Segment updated successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update segment');
      }
    } catch (err) {
      setError('Failed to update segment');
    } finally {
      setLoading(false);
    }
  };

  const deleteSegment = async (segmentId: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this segment?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/route-management/segments/${segmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok && currentRoute) {
        await fetchRouteForLine(currentRoute.line_id);
      } else {
        setError('Failed to delete segment');
      }
    } catch (err) {
      setError('Failed to delete segment');
    } finally {
      setLoading(false);
    }
  };

  // Effect hooks
  useEffect(() => {
    fetchRegions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRegion !== null) {
      fetchCompanies();
    } else {
      setCompanies([]);
      setSelectedCompany(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  useEffect(() => {
    fetchLines();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, selectedCompany]);

  useEffect(() => {
    if (selectedLine) {
      fetchStations(selectedLine);
      fetchRouteForLine(selectedLine);
      // Load intersection data when a line is selected
      fetchIntersectionPoints();
      fetchIntersectionSegments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLine]);

  const getStationName = (stationId: number) => {
    const station = stations.find(s => s.id === stationId);
    if (station) {
      return station.code ? `${station.code} (${station.name})` : station.name;
    }
    return `Station ${stationId}`;
  };

  // Intersection management functions
  const fetchIntersectionPoints = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.INTERSECTION_POINTS));
      if (response.ok) {
        const data = await response.json();
        setIntersectionPoints(data);
      }
    } catch (err) {
      setError('Failed to fetch intersection points');
    }
  };

  const fetchIntersectionSegments = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.INTERSECTION_SEGMENTS));
      if (response.ok) {
        const data = await response.json();
        setIntersectionSegments(data);
      }
    } catch (err) {
      setError('Failed to fetch intersection segments');
    }
  };

  // Removed unused functions: handleCreateIntersectionPoint and handleCreateIntersectionSegment

  const updateIntersectionPoint = async () => {
    if (!editingIntersectionPoint) return;

    setLoading(true);
    try {
      const payload = {
        name: editingIntersectionPoint.name,
        description: editingIntersectionPoint.description,
        lat: editingIntersectionPoint.lat || null,
        long: editingIntersectionPoint.long || null,
        station_codes: editingIntersectionPoint.station_codes || null,
        accessibility_features: editingIntersectionPoint.accessibility_features || null
      };

      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.INTERSECTION_POINT_BY_ID(editingIntersectionPoint.id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await fetchIntersectionPoints();
        setEditingIntersectionPoint(null);
        alert('Intersection point updated successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update intersection point');
      }
    } catch (err) {
      setError('Failed to update intersection point');
    } finally {
      setLoading(false);
    }
  };

  const deleteIntersectionPoint = async (pointId: number) => {
    if (window.confirm('Are you sure you want to delete this intersection point?')) {
      try {
        const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.INTERSECTION_POINT_BY_ID(pointId)), {
          method: 'DELETE',
        });

        if (response.ok) {
          fetchIntersectionPoints();
          fetchIntersectionSegments();
        } else {
          setError('Failed to delete intersection point');
        }
      } catch (err) {
        setError('Failed to delete intersection point');
      }
    }
  };

  // Removed unused function: deleteIntersectionSegment


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <RouteIcon className="h-8 w-8 mr-2" />
          Route Management
        </h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
          <button
            onClick={() => setError(null)}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">Filter Train Lines</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
            <select
              value={selectedRegion || ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedRegion(value ? parseInt(value) : null);
                setSelectedLine(null); // Reset selected line when filter changes
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Regions</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}, {region.country}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
            <select
              value={selectedCompany || ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedCompany(value ? parseInt(value) : null);
                setSelectedLine(null); // Reset selected line when filter changes
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!selectedRegion}
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name} ({company.code})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSelectedRegion(null);
                setSelectedCompany(null);
                setSelectedLine(null);
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {selectedRegion && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-700">
              <strong>Active Filter:</strong> {regions.find(r => r.id === selectedRegion)?.name}
              {selectedCompany && (
                <span>, {companies.find(c => c.id === selectedCompany)?.name}</span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Line Selection */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">
          Select Train Line
          {lines.length > 0 && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({lines.length} line{lines.length !== 1 ? 's' : ''} found)
            </span>
          )}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lines.map((line) => (
            <button
              key={line.id}
              onClick={() => setSelectedLine(line.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedLine === line.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: line.color }}
                />
                <div className="flex-1">
                  <div className="font-medium">{line.name}</div>
                  <div className="text-sm text-gray-500">{line.company_name}</div>
                  {line.region_name && (
                    <div className="text-xs text-gray-400">
                      {line.region_name}{line.region_country && `, ${line.region_country}`}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {lines.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-500">
              {selectedRegion || selectedCompany ? (
                <div>
                  <p>No train lines found for the selected filters.</p>
                  <p className="text-sm mt-1">Try adjusting your region or company selection.</p>
                </div>
              ) : (
                <p>No train lines available.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Route Management */}
      {selectedLine && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              Route for {lines.find(l => l.id === selectedLine)?.name}
            </h2>
            {!currentRoute && (
              <button
                onClick={() => setShowCreateRoute(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Create Route</span>
              </button>
            )}
          </div>

          {/* Create Route Form */}
          {showCreateRoute && (
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="font-medium mb-3">Create New Route</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Route name"
                  value={routeForm.name}
                  onChange={(e) => setRouteForm({ ...routeForm, name: e.target.value })}
                  className="border border-gray-300 rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Description"
                  value={routeForm.description}
                  onChange={(e) => setRouteForm({ ...routeForm, description: e.target.value })}
                  className="border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={createRoute}
                  disabled={loading || !routeForm.name}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 inline mr-1" />
                  Create
                </button>
                <button
                  onClick={() => setShowCreateRoute(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Current Route Display */}
          {currentRoute && (
            <div className="space-y-4">
              {/* Route Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Route Name</div>
                    <div className="font-medium">{currentRoute.name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Distance</div>
                    <div className="font-medium">
                      {currentRoute.total_distance_km ? `${currentRoute.total_distance_km} km` : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Duration</div>
                    <div className="font-medium">
                      {currentRoute.total_duration_minutes ? `${currentRoute.total_duration_minutes} min` : 'N/A'}
                    </div>
                  </div>
                </div>

              </div>

              {/* Add Segment Button */}
              <div className="flex justify-between items-center">
                <h3 className="text-md font-semibold">Route Segments</h3>
                <button
                  onClick={() => setShowAddSegment(true)}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Segment</span>
                </button>
              </div>

              {/* Add Segment Form */}
              {showAddSegment && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Add Route Segment</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <select
                      value={segmentForm.from_station_id}
                      onChange={(e) => setSegmentForm({ ...segmentForm, from_station_id: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="">From Station</option>
                      {stations.map(station => (
                        <option key={station.id} value={station.id}>
                          {station.code ? `${station.code} (${station.name})` : station.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={segmentForm.to_station_id}
                      onChange={(e) => setSegmentForm({ ...segmentForm, to_station_id: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="">To Station</option>
                      {stations.map(station => (
                        <option key={station.id} value={station.id}>
                          {station.code ? `${station.code} (${station.name})` : station.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="Distance (km)"
                      value={segmentForm.distance_km}
                      onChange={(e) => setSegmentForm({ ...segmentForm, distance_km: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-2"
                    />
                    <input
                      type="number"
                      placeholder="Duration (min)"
                      value={segmentForm.duration_minutes}
                      onChange={(e) => setSegmentForm({ ...segmentForm, duration_minutes: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-2"
                    />
                    <select
                      value={segmentForm.transport_type}
                      onChange={(e) => setSegmentForm({ ...segmentForm, transport_type: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="train">Train</option>
                      <option value="bus">Bus</option>
                      <option value="walk">Walk</option>
                    </select>
                  </div>
                  <div className="flex space-x-2 mt-3">
                    <button
                      onClick={addSegment}
                      disabled={loading || !segmentForm.from_station_id || !segmentForm.to_station_id}
                      className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4 inline mr-1" />
                      Save Segment
                    </button>
                    <button
                      onClick={() => setShowAddSegment(false)}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Segments List */}
              <div className="space-y-2">
                {currentRoute.route_segments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Navigation className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No route segments yet. Add some segments to build your route.</p>
                  </div>
                ) : (
                  currentRoute.route_segments.map((segment, index) => (
                    <div key={segment.id} className="border border-gray-200 rounded-lg p-4">
                      {editingSegment?.id === segment.id ? (
                        // Edit Mode
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900">Edit Route Segment</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <select
                              value={editingSegment.from_station_id}
                              onChange={(e) => setEditingSegment({
                                ...editingSegment,
                                from_station_id: parseInt(e.target.value)
                              })}
                              className="border border-gray-300 rounded px-3 py-2"
                            >
                              <option value="">From Station</option>
                              {stations.map(station => (
                                <option key={station.id} value={station.id}>
                                  {station.code ? `${station.code} (${station.name})` : station.name}
                                </option>
                              ))}
                            </select>
                            <select
                              value={editingSegment.to_station_id}
                              onChange={(e) => setEditingSegment({
                                ...editingSegment,
                                to_station_id: parseInt(e.target.value)
                              })}
                              className="border border-gray-300 rounded px-3 py-2"
                            >
                              <option value="">To Station</option>
                              {stations.map(station => (
                                <option key={station.id} value={station.id}>
                                  {station.code ? `${station.code} (${station.name})` : station.name}
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              step="0.1"
                              placeholder="Distance (km)"
                              value={editingSegment.distance_km}
                              onChange={(e) => setEditingSegment({
                                ...editingSegment,
                                distance_km: e.target.value
                              })}
                              className="border border-gray-300 rounded px-3 py-2"
                            />
                            <input
                              type="number"
                              placeholder="Duration (min)"
                              value={editingSegment.duration_minutes}
                              onChange={(e) => setEditingSegment({
                                ...editingSegment,
                                duration_minutes: parseInt(e.target.value)
                              })}
                              className="border border-gray-300 rounded px-3 py-2"
                            />
                            <select
                              value={editingSegment.transport_type}
                              onChange={(e) => setEditingSegment({
                                ...editingSegment,
                                transport_type: e.target.value
                              })}
                              className="border border-gray-300 rounded px-3 py-2"
                            >
                              <option value="train">Train</option>
                              <option value="bus">Bus</option>
                              <option value="walk">Walk</option>
                            </select>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => updateSegment(editingSegment)}
                              disabled={loading}
                              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
                            >
                              <Save className="h-4 w-4 inline mr-1" />
                              Save Changes
                            </button>
                            <button
                              onClick={() => setEditingSegment(null)}
                              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-medium">
                              {segment.segment_order}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <MapPin className="h-4 w-4 text-green-500" />
                                <span className="font-medium">
                                  {segment.from_station_name || getStationName(segment.from_station_id)}
                                </span>
                                <span className="text-gray-400">→</span>
                                <MapPin className="h-4 w-4 text-red-500" />
                                <span className="font-medium">
                                  {segment.to_station_name || getStationName(segment.to_station_id)}
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Navigation className="h-3 w-3 mr-1" />
                                  {segment.distance_km} km
                                </span>
                                <span className="flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {segment.duration_minutes} min
                                </span>
                                <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                                  {segment.transport_type}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => moveSegment(segment.id, 'up')}
                              disabled={loading || index === 0}
                              className="p-1 text-gray-400 hover:text-blue-500 disabled:opacity-30"
                              title="Move up"
                            >
                              <ArrowUp className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => moveSegment(segment.id, 'down')}
                              disabled={loading || index === currentRoute.route_segments.length - 1}
                              className="p-1 text-gray-400 hover:text-blue-500 disabled:opacity-30"
                              title="Move down"
                            >
                              <ArrowDown className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setEditingSegment(segment)}
                              disabled={loading}
                              className="p-1 text-gray-400 hover:text-orange-500 disabled:opacity-30"
                              title="Edit segment"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteSegment(segment.id)}
                              disabled={loading}
                              className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30"
                              title="Delete segment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Intersection Points for this Route */}
              <div className="space-y-4 mt-8">
                <div className="flex justify-between items-center">
                  <h3 className="text-md font-semibold flex items-center">
                    <ArrowRightLeft className="h-5 w-5 mr-2" />
                    Intersection Points
                  </h3>
                  <button
                    onClick={() => setShowAddIntersection(true)}
                    className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Intersection</span>
                  </button>
                </div>

                {/* Add Intersection Form */}
                {showAddIntersection && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-purple-900 mb-3">Add Intersection Point & Segment</h4>

                    {/* Step 1: Create Intersection Point */}
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-purple-800 mb-2">1. Intersection Point Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Intersection Point Name (e.g., Siam Interchange)"
                          value={pointForm.name}
                          onChange={(e) => setPointForm({...pointForm, name: e.target.value})}
                          className="border border-purple-300 rounded px-3 py-2"
                          required
                        />
                        <input
                          type="text"
                          placeholder="Description"
                          value={pointForm.description}
                          onChange={(e) => setPointForm({...pointForm, description: e.target.value})}
                          className="border border-purple-300 rounded px-3 py-2"
                        />
                      </div>
                    </div>

                    {/* Step 2: Create Intersection Segment */}
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-purple-800 mb-3">2. Connection Between Lines</h5>

                      {/* Current Line & Other Line Selection */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="bg-blue-50 p-3 rounded border border-blue-200">
                          <h6 className="text-sm font-medium text-blue-800 mb-2">Current Line</h6>
                          <div className="text-sm text-blue-700 mb-2">
                            <span className="font-medium">{lines.find(l => l.id === selectedLine)?.name}</span>
                            <span className="ml-2 px-2 py-1 bg-blue-200 text-blue-800 rounded text-xs">
                              {currentRoute?.route_segments.length || 0} stations
                            </span>
                          </div>
                          <select
                            value={intersectionSegmentForm.from_station_id}
                            onChange={(e) => setIntersectionSegmentForm({...intersectionSegmentForm, from_station_id: e.target.value})}
                            className="border border-blue-300 rounded px-3 py-2 w-full"
                            required
                          >
                            <option value="">Choose station from current line</option>
                            {(() => {
                              // Create unique list of stations from route segments
                              const uniqueStations = new Map();
                              currentRoute?.route_segments.forEach((segment) => {
                                // Add from station
                                uniqueStations.set(segment.from_station_id, {
                                  id: segment.from_station_id,
                                  name: segment.from_station_name || getStationName(segment.from_station_id)
                                });
                                // Add to station
                                uniqueStations.set(segment.to_station_id, {
                                  id: segment.to_station_id,
                                  name: segment.to_station_name || getStationName(segment.to_station_id)
                                });
                              });

                              return Array.from(uniqueStations.values()).map((station) => (
                                <option key={station.id} value={station.id}>
                                  {station.name}
                                </option>
                              ));
                            })()}
                          </select>
                        </div>

                        <div className="bg-green-50 p-3 rounded border border-green-200">
                          <h6 className="text-sm font-medium text-green-800 mb-2">Other Line</h6>
                          <div className="mb-2">
                            <select
                              value={selectedOtherLine || ''}
                              onChange={async (e) => {
                                const lineId = parseInt(e.target.value);
                                setSelectedOtherLine(lineId);
                                setIntersectionSegmentForm({...intersectionSegmentForm, to_line_id: e.target.value, to_station_id: ''});
                                if (lineId) {
                                  await fetchOtherLineStations(lineId);
                                }
                              }}
                              className="border border-green-300 rounded px-3 py-2 w-full"
                              required
                            >
                              <option value="">Select other line first</option>
                              {lines.filter(line => line.id !== selectedLine).map((line) => (
                                <option key={line.id} value={line.id}>
                                  {line.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <select
                            value={intersectionSegmentForm.to_station_id}
                            onChange={(e) => setIntersectionSegmentForm({...intersectionSegmentForm, to_station_id: e.target.value})}
                            className="border border-green-300 rounded px-3 py-2 w-full"
                            required
                            disabled={!selectedOtherLine}
                          >
                            <option value="">Choose station from other line</option>
                            {otherLineStations.map((station) => (
                              <option key={station.id} value={station.id}>
                                {station.code ? `${station.code} (${station.name})` : station.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Transfer Details */}
                      <div className="bg-gray-50 p-3 rounded border border-gray-200">
                        <h6 className="text-sm font-medium text-gray-800 mb-2">Transfer Details</h6>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Distance</label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.2"
                              value={intersectionSegmentForm.distance_km}
                              onChange={(e) => setIntersectionSegmentForm({...intersectionSegmentForm, distance_km: e.target.value})}
                              className="border border-gray-300 rounded px-3 py-2 w-full"
                              required
                            />
                            <span className="text-xs text-gray-500">km</span>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Walking Time</label>
                            <input
                              type="number"
                              placeholder="5"
                              value={intersectionSegmentForm.duration_minutes}
                              onChange={(e) => setIntersectionSegmentForm({...intersectionSegmentForm, duration_minutes: e.target.value})}
                              className="border border-gray-300 rounded px-3 py-2 w-full"
                              required
                            />
                            <span className="text-xs text-gray-500">minutes</span>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Transport Type</label>
                            <select
                              value={intersectionSegmentForm.transport_type}
                              onChange={(e) => setIntersectionSegmentForm({...intersectionSegmentForm, transport_type: e.target.value})}
                              className="border border-gray-300 rounded px-3 py-2 w-full"
                            >
                              <option value="walk">Walk</option>
                              <option value="underground">Underground</option>
                              <option value="elevator">Elevator</option>
                              <option value="bus">Bus</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Validation Status */}
                    <div className="mb-3 p-2 bg-gray-50 rounded border">
                      <h6 className="text-xs font-medium text-gray-700 mb-1">Required Fields Status:</h6>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                        <div className={`flex items-center ${pointForm.name ? 'text-green-600' : 'text-red-600'}`}>
                          <span className="mr-1">{pointForm.name ? '✓' : '✗'}</span>
                          Intersection Name
                        </div>
                        <div className={`flex items-center ${intersectionSegmentForm.from_station_id ? 'text-green-600' : 'text-red-600'}`}>
                          <span className="mr-1">{intersectionSegmentForm.from_station_id ? '✓' : '✗'}</span>
                          Current Line Station
                        </div>
                        <div className={`flex items-center ${selectedOtherLine ? 'text-green-600' : 'text-red-600'}`}>
                          <span className="mr-1">{selectedOtherLine ? '✓' : '✗'}</span>
                          Other Line Selected
                        </div>
                        <div className={`flex items-center ${intersectionSegmentForm.to_station_id ? 'text-green-600' : 'text-red-600'}`}>
                          <span className="mr-1">{intersectionSegmentForm.to_station_id ? '✓' : '✗'}</span>
                          Other Line Station
                        </div>
                        <div className={`flex items-center ${intersectionSegmentForm.distance_km ? 'text-green-600' : 'text-red-600'}`}>
                          <span className="mr-1">{intersectionSegmentForm.distance_km ? '✓' : '✗'}</span>
                          Distance
                        </div>
                        <div className={`flex items-center ${intersectionSegmentForm.duration_minutes ? 'text-green-600' : 'text-red-600'}`}>
                          <span className="mr-1">{intersectionSegmentForm.duration_minutes ? '✓' : '✗'}</span>
                          Duration
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={async () => {
                          if (!pointForm.name || !intersectionSegmentForm.from_station_id || !intersectionSegmentForm.to_station_id || !intersectionSegmentForm.to_line_id || !intersectionSegmentForm.distance_km || !intersectionSegmentForm.duration_minutes) {
                            setError('Please fill in all required fields (check status above)');
                            return;
                          }

                          setLoading(true);
                          try {
                            // Step 1: Create intersection point
                            const pointData = {
                              name: pointForm.name,
                              description: pointForm.description || null,
                              lat: pointForm.lat ? parseFloat(pointForm.lat) : null,
                              long: pointForm.long ? parseFloat(pointForm.long) : null,
                              station_codes: pointForm.station_codes ? pointForm.station_codes.split(',').map(s => s.trim()) : null,
                              accessibility_features: pointForm.accessibility_features ?
                                (() => {
                                  try {
                                    return JSON.parse(pointForm.accessibility_features);
                                  } catch {
                                    return { description: pointForm.accessibility_features };
                                  }
                                })() : null
                            };

                            const pointResponse = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.INTERSECTION_POINTS), {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(pointData),
                            });

                            if (!pointResponse.ok) {
                              throw new Error('Failed to create intersection point');
                            }

                            const createdPoint = await pointResponse.json();

                            // Step 2: Create intersection segment with the new point ID
                            const segmentData = {
                              intersection_point_id: createdPoint.id,
                              from_station_id: parseInt(intersectionSegmentForm.from_station_id),
                              to_station_id: parseInt(intersectionSegmentForm.to_station_id),
                              from_line_id: selectedLine || parseInt(intersectionSegmentForm.from_line_id),
                              to_line_id: parseInt(intersectionSegmentForm.to_line_id),
                              distance_km: parseFloat(intersectionSegmentForm.distance_km),
                              duration_minutes: parseInt(intersectionSegmentForm.duration_minutes),
                              transport_type: intersectionSegmentForm.transport_type,
                              transfer_cost: parseFloat(intersectionSegmentForm.transfer_cost),
                              direction_instructions: intersectionSegmentForm.direction_instructions || null,
                              accessibility_notes: intersectionSegmentForm.accessibility_notes || null
                            };

                            const segmentResponse = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.INTERSECTION_SEGMENTS), {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(segmentData),
                            });

                            if (!segmentResponse.ok) {
                              throw new Error('Failed to create intersection segment');
                            }

                            // Reset forms and refresh data
                            setPointForm({
                              name: '',
                              description: '',
                              lat: '',
                              long: '',
                              station_codes: '',
                              accessibility_features: ''
                            });
                            setIntersectionSegmentForm({
                              intersection_point_id: '',
                              from_station_id: '',
                              to_station_id: '',
                              from_line_id: '',
                              to_line_id: '',
                              distance_km: '',
                              duration_minutes: '',
                              transport_type: 'walk',
                              transfer_cost: '0.00',
                              direction_instructions: '',
                              accessibility_notes: ''
                            });

                            // Reset other line selection
                            setSelectedOtherLine(null);
                            setOtherLineStations([]);

                            await fetchIntersectionPoints();
                            await fetchIntersectionSegments();
                            setShowAddIntersection(false);
                            alert('Intersection added successfully!');

                          } catch (error) {
                            console.error('Error creating intersection:', error);
                            setError('Failed to create intersection. Please try again.');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading || !pointForm.name || !intersectionSegmentForm.from_station_id || !intersectionSegmentForm.to_station_id || !intersectionSegmentForm.to_line_id || !intersectionSegmentForm.distance_km || !intersectionSegmentForm.duration_minutes}
                        className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
                      >
                        <Plus className="h-4 w-4 inline mr-1" />
                        {loading ? 'Adding...' : 'Add Intersection'}
                      </button>
                      <button
                        onClick={() => setShowAddIntersection(false)}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Intersection Points List */}
                <div className="space-y-2">
                  {intersectionPoints.filter(point =>
                    // Filter points that have segments connecting to stations in this route
                    intersectionSegments.some(segment =>
                      currentRoute.route_segments.some(routeSegment =>
                        segment.from_station_id === routeSegment.from_station_id ||
                        segment.from_station_id === routeSegment.to_station_id ||
                        segment.to_station_id === routeSegment.from_station_id ||
                        segment.to_station_id === routeSegment.to_station_id
                      ) && segment.intersection_point_id === point.id
                    )
                  ).map((point) => (
                    <div key={point.id} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      {editingIntersectionPoint?.id === point.id ? (
                        // Edit Mode
                        <div className="space-y-4">
                          <h4 className="font-medium text-purple-900">Edit Intersection Point</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                              type="text"
                              placeholder="Point name"
                              value={editingIntersectionPoint.name}
                              onChange={(e) => setEditingIntersectionPoint({ ...editingIntersectionPoint, name: e.target.value })}
                              className="border border-gray-300 rounded px-3 py-2"
                            />
                            <input
                              type="text"
                              placeholder="Description"
                              value={editingIntersectionPoint.description}
                              onChange={(e) => setEditingIntersectionPoint({ ...editingIntersectionPoint, description: e.target.value })}
                              className="border border-gray-300 rounded px-3 py-2"
                            />
                            <input
                              type="number"
                              step="0.000001"
                              placeholder="Latitude"
                              value={editingIntersectionPoint.lat || ''}
                              onChange={(e) => setEditingIntersectionPoint({ ...editingIntersectionPoint, lat: e.target.value ? parseFloat(e.target.value) : undefined })}
                              className="border border-gray-300 rounded px-3 py-2"
                            />
                            <input
                              type="number"
                              step="0.000001"
                              placeholder="Longitude"
                              value={editingIntersectionPoint.long || ''}
                              onChange={(e) => setEditingIntersectionPoint({ ...editingIntersectionPoint, long: e.target.value ? parseFloat(e.target.value) : undefined })}
                              className="border border-gray-300 rounded px-3 py-2"
                            />
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={updateIntersectionPoint}
                              disabled={loading}
                              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50 flex items-center space-x-1"
                            >
                              <Save className="h-4 w-4" />
                              <span>Save Changes</span>
                            </button>
                            <button
                              onClick={() => setEditingIntersectionPoint(null)}
                              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <ArrowRightLeft className="h-4 w-4 text-purple-600" />
                              <span className="font-medium text-purple-900">{point.name}</span>
                            </div>
                            <p className="text-sm text-purple-700 mb-2">{point.description}</p>
                            {point.lat && point.long && (
                              <p className="text-xs text-gray-500 mb-2">
                                Coordinates: {point.lat}, {point.long}
                              </p>
                            )}

                            {/* Show connected segments for this intersection point */}
                            <div className="space-y-1">
                              {intersectionSegments
                                .filter(segment => segment.intersection_point_id === point.id)
                                .filter(segment =>
                                  // Only show segments that connect to stations in this route
                                  currentRoute.route_segments.some(routeSegment =>
                                    segment.from_station_id === routeSegment.from_station_id ||
                                    segment.from_station_id === routeSegment.to_station_id ||
                                    segment.to_station_id === routeSegment.from_station_id ||
                                    segment.to_station_id === routeSegment.to_station_id
                                  )
                                )
                                .map(segment => (
                                  <div key={segment.id} className="text-xs bg-white rounded px-2 py-1 border border-purple-200">
                                    <span className="font-medium">{segment.from_station_name}</span>
                                    <span className="text-purple-600 mx-1">({segment.from_line_name})</span>
                                    <span className="text-gray-400 mx-1">↔</span>
                                    <span className="font-medium">{segment.to_station_name}</span>
                                    <span className="text-purple-600 mx-1">({segment.to_line_name})</span>
                                    <span className="text-gray-500 ml-2">
                                      {segment.distance_km}km • {segment.duration_minutes}min • {segment.transport_type}
                                    </span>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => setEditingIntersectionPoint(point)}
                              className="text-purple-600 hover:text-purple-800 p-1"
                              title="Edit intersection point"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteIntersectionPoint(point.id)}
                              className="text-purple-600 hover:text-purple-800 p-1"
                              title="Delete intersection point"
                              >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {intersectionPoints.filter(point =>
                    intersectionSegments.some(segment =>
                      currentRoute.route_segments.some(routeSegment =>
                        segment.from_station_id === routeSegment.from_station_id ||
                        segment.from_station_id === routeSegment.to_station_id ||
                        segment.to_station_id === routeSegment.from_station_id ||
                        segment.to_station_id === routeSegment.to_station_id
                      ) && segment.intersection_point_id === point.id
                    )
                  ).length === 0 && (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <ArrowRightLeft className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No intersection points found for this route.</p>
                      <p className="text-sm">Add intersection points to show transfers to other lines.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {selectedLine && !currentRoute && !showCreateRoute && (
            <div className="text-center py-8 text-gray-500">
              <RouteIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No route found for this line. Create a route to get started.</p>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Processing...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteManagementManager;